import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

export const CTransaction = () => {
    const location = useLocation();
    const history = useHistory();
    const domain = location.state?.StatusCheckURL;
    const redirectUrl = location.state?.FormActionURL;
    const clientId = location.state?.MerchantID;
    const orderId = location.state?.OrderID;
    const amount = location.state?.Amount;
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [paymentInitiated, setPaymentInitiated] = useState(false);
    const [error, setError] = useState('');
    const redirectToVerification = useCallback((status) => {
        window.location.href = `/verify-order?orderId=${orderId}&amount=${amount}&status=${status}&clientId=${clientId}`;
    }, [orderId, amount, clientId]);

    useEffect(() => {
        if (!domain || !orderId || !clientId) {
            setError('Missing required payment parameters');
            return;
        }
        const script = document.createElement('script');

        let scriptUrl = domain;
        if (scriptUrl.endsWith('/pg')) {
            scriptUrl = scriptUrl.replace('/pg', '');
        }
        const finalScriptSrc = `${scriptUrl}`;
        script.src = finalScriptSrc;

        script.onload = () => {
            setScriptLoaded(true);
        };

        script.onerror = () => {
            setError('Failed to load payment gateway script');
        };

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [domain, orderId, clientId]);

    useEffect(() => {
        if (scriptLoaded && !paymentInitiated && !error) {
            handlePayment();
        }
    }, [scriptLoaded, paymentInitiated, error]);

    const handlePayment = useCallback(() => {
        if (paymentInitiated) return;

        if (!window.checkout) {
            setError('Payment gateway not available');
            setTimeout(() => redirectToVerification('error'), 3000);
            return;
        }

        try {
            setPaymentInitiated(true);

            const options = {
                orderId,
                redirectUrl,
                clientId
            };

            window.checkout(options);

        } catch (err) {
            setError('Payment initiation failed');
            setPaymentInitiated(false);
            setTimeout(() => redirectToVerification('error'), 3000);
        }
    }, [paymentInitiated, orderId, redirectUrl, clientId, redirectToVerification]);

    if (error) {
        return (
            history.push('/PaymentGatewayTransaction')
        );
    }

    return (
        <div className="max-w-md mx-auto mt-8 p-6 bg-white border border-gray-200 rounded-lg shadow-md">
            <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-800 mb-6">CredoPay Checkout</h1>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <p className="mb-2"><strong>Order ID:</strong> {orderId}</p>
                    <p className="mb-2"><strong>Amount:</strong> ₹{amount}</p>
                    <p><strong>Payment Gateway:</strong> CredoPay</p>
                </div>

                {!scriptLoaded && (
                    <div className="mb-6">
                        <p className="mb-4">Loading CredoPay payment gateway...</p>
                        <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    </div>
                )}

                {paymentInitiated && (
                    <div className="mb-6">
                        <div className="text-green-600 text-6xl mb-4">✅</div>
                        <p className="text-green-700 font-semibold mb-2">Payment initiated successfully!</p>
                        <p className="text-gray-600 mb-2">Redirecting to CredoPay payment gateway...</p>
                        <p className="text-sm text-gray-500">
                            If you are not redirected automatically, please check your browser settings.
                        </p>
                    </div>
                )}

                {scriptLoaded && !paymentInitiated && (
                    <div className="space-y-3">
                        <button
                            onClick={handlePayment}
                            className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg w-full"
                        >
                            Pay Now ₹{amount}
                        </button>
                        <button
                            onClick={() => redirectToVerification('manual')}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full"
                        >
                            Pay
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
